import { IonPage, useIonModal } from '@ionic/react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useCreateMyMonitoringAccount } from '@apps-common/apis/portal/my-monitoring'
import { CardContainer, NoResultsModalAddCard } from '@apps-common/components/common'
import { PageSubtitle, PageTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundedSm, ButtonRoundSm, FieldErrorMessage, InputSM, SelectInputSM } from '@apps-common/components/elements'
import { Header, LayoutContainer, LayoutPadding, VertSpacerLg, VertSpacerMd, VertSpacerXl } from '@apps-common/components/layout'
import { ModalButton, PortalUnitSelectModal } from '@apps-common/components/modals'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED, formEmailPatternInvalid, formPhonePatternInvalid } from '@apps-common/config/react-hook-form'
import { MY_MONITORING_ROLE_OPTION_USER, MY_MONITORING_ROLES_OPTIONS } from '@apps-common/config/user-roles'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { toastError } from '@apps-common/lib/toast'
import { DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS } from '@apps-common/stores'
import { IMyMonitoringAccountModel, IMyMonitoringAccountUnitItemModel } from '@apps-common/types/portal/my-monitoring'
import {
  ROUTE_MY_MONITORING_ACCOUNT_CREATE,
  ROUTE_MY_MONITORING_ACCOUNT_DETAILS
} from '@apps-radionet-portal-root/src/config/private-routes'

import { MyMonitoringAccountUnitCard, MyMonitoringUnitAddModal } from './components'

interface IFormProps {
  account: IMyMonitoringAccountModel
  units: IMyMonitoringAccountUnitItemModel[]
}

export const MyMonitoringAccountCreatePage = () => {
  const { navigateReplace } = useNavigation()
  const [currentAddingUnit, setCurrentAddingUnit] = useState<IMyMonitoringAccountUnitItemModel>()

  const { isPending: createAccountPending, mutate: createAccount } = useCreateMyMonitoringAccount(true, (data) => {
    navigateReplace({
      routeUrl: ROUTE_MY_MONITORING_ACCOUNT_DETAILS.buildPath(data.id),
      route: ROUTE_MY_MONITORING_ACCOUNT_DETAILS
    })
  })

  const methods = useForm<IFormProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      account: {
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        role: MY_MONITORING_ROLE_OPTION_USER.value
      },
      units: []
    }
  })

  const { register, handleSubmit, formState, watch, setValue, reset } = methods
  const { isSubmitting, errors } = formState

  const unitsVal = watch('units')

  const [presentAddUnit, dismissAddUnit] = useIonModal(MyMonitoringUnitAddModal, {
    componentProps: currentAddingUnit,
    onDismiss: (data: string, role: string) => {
      if (role === ModalDismiss.Confirm) {
        try {
          const unit = JSON.parse(data)

          setValue('units', [...(unitsVal || []), unit], { shouldValidate: true })
        } catch {
          toastError('Failed to add device.')
        }
      }
      dismissAddUnit(data, role)
      setCurrentAddingUnit(undefined)
    },
    enableBackdropDismiss: true
  })

  const modalProps = {
    exclusions: {
      ids: unitsVal?.map?.((d) => d.unitId),
      component: (
        <div className='jost-light text-[0.85em] bg-warning text-on-warning border border-warning flex items-center justify-between grow px-[10px] py-[5px] rounded-full'>
          Device already added <commonIcons.toastWarning className='text-[1.2em]' />
        </div>
      )
    },
    filterDefaults: {
      state: {
        ...DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS,
        hasNoActivation: true,
        isActivated: true
      },
      filter: {
        hideStatus: true,
        hideIsActivated: true,
        hideReset: true
      }
    }
  }

  const modalCallback = (d: any) => {
    if (d) {
      setCurrentAddingUnit(d)
    }
  }

  useEffect(() => {
    register('units', {
      validate: (value: IMyMonitoringAccountUnitItemModel[]) => {
        if (!value || value.length === 0) {
          return 'At least 1 device is required'
        }

        return true
      }
    })
  }, [])

  useEffect(() => {
    if (currentAddingUnit) {
      presentAddUnit()
    }
  }, [currentAddingUnit])

  const submit = (values: IFormProps) => createAccount({ account: values.account, units: values.units })

  const loading = false
  const updating = isSubmitting || createAccountPending

  return (
    <IonPage>
      <Header>
        <Navbar showBack transparent icons={[]} />
        <Breadcrumbs route={ROUTE_MY_MONITORING_ACCOUNT_CREATE} />
        <PageTitle>Create account</PageTitle>
      </Header>
      <LayoutContainer>
        <LayoutPadding classes='max-w-[750px]'>
          <form id='my-monitoring-user-create-form' noValidate onSubmit={handleSubmit(submit)}>
            <CardContainer bgClasses='bg-surface border border-border'>
              <PageSubtitle classes='text-text px-[5px]'>Details</PageSubtitle>
              <VertSpacerMd />
              <div className='grid sm:grid-cols-2 gap-x-[20px] gap-y-[10px]'>
                <InputSM
                  key='email'
                  type='text'
                  placeholder='User account email'
                  label='Email'
                  id='email'
                  showLabel
                  containerClassName='sm:col-span-2'
                  loading={loading}
                  disabled={updating}
                  info='*Email cannot be changed after creation, please confirm email is correct before creating account.'
                  registration={register('account.email', {
                    required: FORM_DEFAULT_REQUIRED,
                    pattern: formEmailPatternInvalid
                  })}
                  errors={errors?.account?.email}
                />
                <InputSM
                  key='firstName'
                  type='text'
                  placeholder='User first name'
                  label='First name'
                  id='firstName'
                  showLabel
                  loading={loading}
                  disabled={updating}
                  registration={register('account.firstName', {
                    required: FORM_DEFAULT_REQUIRED
                  })}
                  errors={errors?.account?.firstName}
                />
                <InputSM
                  key='lastName'
                  type='text'
                  placeholder='User last name'
                  label='Last name'
                  id='lastName'
                  showLabel
                  loading={loading}
                  disabled={updating}
                  registration={register('account.lastName', {
                    required: FORM_DEFAULT_REQUIRED
                  })}
                  errors={errors?.account?.lastName}
                />
                <InputSM
                  key='phoneNumber'
                  type='text'
                  placeholder='User phone number'
                  label='Phone number'
                  id='phoneNumber'
                  showLabel
                  loading={loading}
                  disabled={updating}
                  registration={register('account.phoneNumber', {
                    required: false,
                    pattern: formPhonePatternInvalid
                  })}
                  errors={errors?.account?.phoneNumber}
                />
                <SelectInputSM
                  type='text'
                  label='Role'
                  id='role'
                  showLabel
                  className='capitalize'
                  loading={loading}
                  disabled={updating}
                  registration={register('account.role', {
                    required: true
                  })}
                  options={MY_MONITORING_ROLES_OPTIONS}
                  errors={errors?.account?.role}
                />
              </div>
            </CardContainer>
            <VertSpacerXl />
            <div className='p-3 pb-3 bg-surface-2 border border-border rounded-3xl'>
              <div className='flex items-center justify-between pl-2'>
                <PageSubtitle classes='text-text'>Devices</PageSubtitle>
                <ModalButton
                  component={PortalUnitSelectModal}
                  disabled={loading || updating}
                  componentProps={modalProps}
                  confirmCallback={modalCallback}
                  enterAnimation={modalSlideUpEnterAnimation}
                  leaveAnimation={modalSlideUpLeaveAnimation}
                  backdropDismiss
                >
                  <ButtonRoundSm type='div' className='action-success'>
                    <commonIcons.add />
                  </ButtonRoundSm>
                </ModalButton>
              </div>
              <VertSpacerLg />
              {(!unitsVal || unitsVal?.length) === 0 && (
                <NoResultsModalAddCard
                  title='No devices added'
                  message='Add a device to this account to continue.'
                  classes1='!bg-surface'
                  disabled={loading || updating}
                  callback={modalCallback}
                  Modal={PortalUnitSelectModal}
                  modalProps={modalProps}
                />
              )}
              {unitsVal && unitsVal?.length > 0 && (
                <div className='grid sm:grid-cols-2 gap-[20px]'>
                  {unitsVal?.map((d) => (
                    <MyMonitoringAccountUnitCard
                      key={`my-monitoring-account-create-unit-${d.unitId}`}
                      disabled={updating}
                      unit={d}
                      updateCallback={(unit: IMyMonitoringAccountUnitItemModel) => {
                        const index = unitsVal?.findIndex((u) => u.unitId === unit.unitId)

                        if (index !== -1) {
                          const cloned = [...unitsVal]

                          cloned[index] = unit
                          setValue('units', cloned, { shouldValidate: true })
                        }
                      }}
                      removeCallback={(unitId: string) => setValue('units', unitsVal?.filter((u) => u.unitId !== unitId) || [])}
                    />
                  ))}
                </div>
              )}
              {errors?.units && <FieldErrorMessage errors={errors?.units as any} />}
            </div>
          </form>
          <VertSpacerLg />
          <ButtonRoundedSm
            type='submit'
            form='my-monitoring-user-create-form'
            loading={loading || updating}
            className='w-full sm:w-auto sm:min-w-[160px] action-success'
            text='Create Account'
          />
        </LayoutPadding>
      </LayoutContainer>
    </IonPage>
  )
}
